import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { getEnvironment } from 'src/lib/featureFlags';

import LogsConnectionHandler from './logsConnectionHandler';
const Context = createContext(null);

const emptyLogs = [];
let currentConnectionHandler = null;
const expectedServicesCount = getEnvironment().logsNumberOfExpectedAllocations;
let doClearAll = () => {};

export const Provider = ({ children }) => {
  const [consumers, setConsumers] = useState(0);
  const [{ logs, requests }, setLogs] = useState({ logs: {}, requests: [] });
  const [status, setStatus] = useState({ status: 'idle', connectedServicesCount: 0, expectedServicesCount });

  const hasConsumers = consumers > 0;

  doClearAll = () => {
    setLogs({ logs: {}, requests: [] });
  };

  useEffect(() => {
    if (hasConsumers) {
      if (!currentConnectionHandler) {
        currentConnectionHandler = new LogsConnectionHandler({
          onMessage(message) {
            message = normalizeMessage(message);
            setLogs(({ logs, requests }) => ({
              logs: applyMessageToLogs(logs, message),
              requests: applyMessageToRequests(requests, message),
            }));
          },

          onStatusChange: (status) => setStatus({ expectedServicesCount, ...status })
        });
      }
    } else if (currentConnectionHandler) {
      currentConnectionHandler.terminate();
      currentConnectionHandler = null;
    }

    // Provider is always mounted, hence this useEffect is always mounted as well.
    // no need to clen up, effect cleans up itself on update.
  }, [hasConsumers]);

  const value = useMemo(() => ({ logs, requests, status, setConsumers }), [logs, requests, status]);

  return (
    <Context.Provider value={value}>
      {children}
    </Context.Provider>
  );
};

const useConsumeAwareContext = () => {
  const value = useContext(Context);
  const { setConsumers } = value;
  useEffect(() => {
    setConsumers(x => x + 1);
    return () => setConsumers(x => x - 1);
  }, [setConsumers]);
  return value;
};

export const useDebugLogsRequestsList = () => {
  const { requests } = useConsumeAwareContext();
  return requests;
};

export const useDebugLogsForRequest = (requestId) => {
  const { logs } = useConsumeAwareContext();
  return logs[requestId] || emptyLogs;
};

export const useDebugConnectionStatus = () => {
  const { status } = useContext(Context);
  return status;
};

////////// private functions //////////

function normalizeMessage(message) {
  message = message || {};
  const newMessage = {
    ...message,
    level: message.level || 'error',
    message: typeof message.message === 'string' ? message.message : JSON.stringify(message.message),
    request_id: message.request_id || '???',
    source: message.source || '???',
    source_id: message.source_id || '???',
    timestamp: message.timestamp || new Date().getTime(),
  };

  return newMessage;
}

function applyMessageToLogs(logs, message) {
  //ignore empty message with request_metadata
  if (message.request_metadata && !message.message) return logs;

  const id = message.request_id;
  return {
    ...logs,
    [id]: [...logs[id] || [], message]
  };
}

function applyMessageToRequests(requests, message) {
  const id = message.request_id;
  const initValue = requests.find(r => r.request_id === id);
  let value = initValue || { request_id: id };

  if (!value.source && message.source) value = { ...value, source: message.source };
  if (!value.source_id && message.source_id) value = { ...value, source_id: message.source_id };
  if (!value.first_message_at && message.timestamp) value = { ...value, first_message_at: message.timestamp };
  if (message.level === 'error' && value.level !== 'error') value = { ...value, level: 'error' };
  if (message.level === 'warn' && value.level !== 'error' && value.level !== 'warn') value = { ...value, level: 'warn' };
  if (message.level === 'stopped') value = { ...value, level: 'stopped' };

  if (message.request_metadata) {
    value = {
      ...message.request_metadata,
      ...value
    };
  }

  if (!initValue) return [...requests, value];
  if (initValue !== value) {
    requests = [...requests];
    requests.splice(requests.indexOf(initValue), 1, value);
    return requests;
  }
  return requests;
}

export const clearAllLogs = () => doClearAll();
